import { useState, useEffect } from 'react'
import {
  Mail,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  FileText,
  Loader2,
  Settings,
  Info,
  ShieldCheck,
  Zap,
  Globe,
  RefreshCw,
  Search
} from 'lucide-react'
import { apiService } from '../config/api'
import toast from 'react-hot-toast'
import { Link } from 'react-router-dom'

export default function Verification() {
  const [email, setEmail] = useState('')
  const [isVerifying, setIsVerifying] = useState(false)
  const [result, setResult] = useState(null)
  const [settingsCheck, setSettingsCheck] = useState(null)

  useEffect(() => {
    checkSettings()
  }, [])

  const checkSettings = async () => {
    try {
      const response = await apiService.checkSettings()
      setSettingsCheck(response.data.data)
    } catch (error) {
      setSettingsCheck({ smtpConfigured: false, scannerConfigured: false })
    }
  }

  const handleSingleVerification = async (e) => {
    e.preventDefault()
    if (!email.trim()) {
      toast.error('Please enter an email address')
      return
    }

    if (!settingsCheck?.scannerConfigured) {
      toast.error('System not configured. Please visit settings.')
      return
    }

    try {
      setIsVerifying(true)
      setResult(null)
      const response = await apiService.verifyEmail(email.trim())
      setResult(response.data.data)
      toast.success('Verification complete')
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Verification failed'
      toast.error(errorMsg)
    } finally {
      setIsVerifying(false)
    }
  }

  const getStatusConfig = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'valid') return { icon: CheckCircle, class: 'status-valid', text: 'Safe & Valid' };
    if (s === 'invalid') return { icon: XCircle, class: 'status-invalid', text: 'Invalid' };
    if (s === 'disposable') return { icon: AlertTriangle, class: 'status-disposable', text: 'Disposable' };
    if (s === 'catch all' || s === 'catch-all') return { icon: AlertTriangle, class: 'status-catch-all', text: 'Catch-All' };
    return { icon: Info, class: 'status-unknown', text: status };
  }

  return (
    <div className="max-w-4xl mx-auto space-y-12">
      {/* Configuration Alert */}
      {settingsCheck && !settingsCheck.scannerConfigured && (
        <div className="card bg-brand-50/50 border-brand-200 dark:bg-brand-500/5 dark:border-brand-800/50 animate-slide-down">
          <div className="card-body flex items-center gap-4">
            <div className="h-10 w-10 bg-brand-100 dark:bg-brand-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
              <Settings className="h-5 w-5 text-brand-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-bold text-slate-900 dark:text-white">System Actions Required</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Please configure Email Scanner settings to enable verification.</p>
            </div>
            <Link to="/settings" className="btn btn-primary py-2 text-xs">Configure Now</Link>
          </div>
        </div>
      )}

      {/* Main Search Section */}
      <div className="text-center space-y-4 mb-2">
        <h1 className="text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight">Single <span className="gradient-text">Validation</span></h1>
        <p className="text-slate-500 dark:text-slate-400 font-medium max-w-lg mx-auto">
          Enterprise-grade email verification with real-time SMTP handshakes and syntax analysis.
        </p>
      </div>

      <div className="relative group max-w-2xl mx-auto animate-fade-in">
        <form onSubmit={handleSingleVerification} className="relative z-10 flex gap-3 p-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[2rem] shadow-premium group-focus-within:shadow-premium-hover transition-all duration-300">
          <div className="flex-1 relative flex items-center gap-3 pl-4">
            <Search className="h-5 w-5 text-slate-400 group-focus-within:text-brand-500 transition-colors" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@company.com"
              className="w-full h-12 bg-transparent border-none focus:ring-0 focus:outline-none text-slate-900 dark:text-white font-medium placeholder-slate-400"
              disabled={isVerifying}
              autoFocus
              autoComplete="off"
            />
          </div>
          <button
            type="submit"
            disabled={isVerifying || !email.trim() || !settingsCheck?.scannerConfigured}
            className="btn btn-primary rounded-2xl h-12 px-8 min-w-[140px]"
          >
            {isVerifying ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Verify Now'}
          </button>
        </form>
        {/* Animated Background Element */}
        <div className="absolute -inset-1 bg-gradient-to-r from-brand-600 to-primary-600 rounded-[2rem] blur opacity-10 group-focus-within:opacity-20 transition-opacity duration-300 -z-10" />
      </div>

      {/* Result Section */}
      {result ? (
        <div className="animate-slide-up space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* Status Card */}
            <div className="md:col-span-5 h-full">
              <div className="card h-full bg-white dark:bg-slate-900 border-2 border-slate-100 dark:border-slate-800">
                <div className="card-body flex flex-col items-center justify-center text-center p-8">
                  {(() => {
                    const cfg = getStatusConfig(result.status);
                    return (
                      <>
                        <div className={`h-20 w-20 rounded-3xl flex items-center justify-center mb-6 shadow-lg ${result.status.toLowerCase() === 'valid' ? 'bg-success-50 dark:bg-success-900/20 text-success-600' :
                          result.status.toLowerCase() === 'invalid' ? 'bg-danger-50 dark:bg-danger-900/20 text-danger-600' :
                            'bg-warning-50 dark:bg-warning-900/20 text-warning-600'
                          }`}>
                          <cfg.icon className="h-10 w-10" />
                        </div>
                        <span className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest border mb-4 ${cfg.class}`}>
                          {cfg.text}
                        </span>
                        <h2 className="text-xl font-bold text-slate-900 dark:text-white break-all">{result.email}</h2>
                        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Verified in {result.duration}ms</p>
                      </>
                    )
                  })()}
                </div>
              </div>
            </div>

            {/* Analysis Details */}
            <div className="md:col-span-7 space-y-6">
              <div className="card">
                <div className="card-header">
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider">Deep Analysis</h3>
                </div>
                <div className="card-body space-y-4">
                  {(result.verification_reason || result.reason) && (
                    <div className="p-4 bg-slate-50 dark:bg-slate-950 rounded-xl flex gap-3">
                      <Info className="h-5 w-5 text-brand-500 flex-shrink-0" />
                      <p className="text-sm text-slate-600 dark:text-slate-400 font-medium leading-relaxed">
                        {result.verification_reason || result.reason}
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    {result.verification_details && Object.entries(result.verification_details).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-100 dark:border-slate-800/10">
                        <span className="text-xs font-bold text-slate-500 capitalize">{key.replace(/_/g, ' ')}</span>
                        <span className={`text-xs font-extrabold ${value === true || value === 'true' || value === 'yes' ? 'text-success-600' : 'text-slate-900 dark:text-white'}`}>
                          {typeof value === 'boolean' ? (value ? 'PASS' : 'FAIL') : value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <details className="group">
              <summary className="cursor-pointer p-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-brand-500" />
                  <span className="text-sm font-bold text-slate-900 dark:text-white">Technical Metadata (JSON)</span>
                </div>
                <div className="text-slate-400 group-open:rotate-180 transition-transform">
                  <RefreshCw className="h-4 w-4" />
                </div>
              </summary>
              <div className="px-6 pb-6 mt-2">
                <pre className="text-xs bg-slate-900 dark:bg-slate-950/50 text-slate-300 p-6 rounded-2xl overflow-x-auto shadow-inner border border-slate-200 dark:border-slate-800 font-mono leading-relaxed">
                  {JSON.stringify(result.details || result, null, 2)}
                </pre>
              </div>
            </details>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12 animate-fade-in">
          {[
            { icon: Zap, title: 'Speed', desc: 'SMTP handshakes in milliseconds' },
            { icon: ShieldCheck, title: 'Security', desc: 'Secure encryption protocols' },
            { icon: Globe, title: 'Global', desc: 'All major ESP support' }
          ].map((feature, i) => (
            <div key={i} className="flex flex-col items-center text-center p-6 space-y-3">
              <div className="h-12 w-12 bg-white dark:bg-slate-900 shadow-premium rounded-2xl flex items-center justify-center text-brand-600">
                <feature.icon className="h-6 w-6" />
              </div>
              <h3 className="font-bold text-slate-900 dark:text-white">{feature.title}</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">{feature.desc}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}